// File: components/SummaryBox.js
import React from 'react';

/**
 * A reusable summary box component for displaying cost and modifier information
 * @param {Object} props
 * @param {string} props.title - Title for the summary box (e.g., "Currently Selected")
 * @param {string} props.description - Optional detailed description
 * @param {Array} props.items - Array of items to display in the summary box
 * @param {string} props.items[].label - Label for the item
 * @param {string|number} props.items[].value - Value for the item
 * @param {string} props.items[].type - Type of item (e.g., "success", "cost", "savings", "neutral", "equity")
 * @param {boolean} props.items[].bold - Whether to bold the item
 * @param {Object} props.summaryBoxInfo - Configuration for text labels
 */
const SummaryBox = ({ title, description, items, summaryBoxInfo }) => {
  // Helper function to determine text color based on item type
  const getTextColorClass = (type, value) => {
    if (type === 'success') {
      return parseFloat(value) >= 0 ? 'text-green-600' : 'text-red-600';
    } else if (type === 'cost') {
      return 'text-red-600';
    } else if (type === 'savings') {
      return 'text-green-600';
    } else if (type === 'equity') {
      return 'text-red-600'; // Assuming equity is always shown as negative (given up)
    }
    return ''; // Default is no special color
  };

  return (
    <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm">
      {title && <p className="font-semibold mb-3">{title}</p>}
      {description && <p className="mb-5">{description}</p>}

      {items.map((item, index) => (
        <div key={index} className="flex justify-between items-center mb-2">
          <span className={item.bold ? "font-medium" : ""}>
            <strong>{item.label}:</strong>
          </span>
          <span className={`ml-4 ${getTextColorClass(item.type, item.value)}`}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default SummaryBox;